import { useCallback, useEffect, useRef, useState } from 'react';
import { scoreResult } from './scoreResults';

const hasScore = value => value !== '' && value != null && Number(value) > 0;

// Explicit confirmations only: no score-array watcher, network poll or draft restore can play a reaction.
export const useScoreReactionSequence = ({ scope, active, enabled, onAdvance }) => {
  const [reaction, setReaction] = useState(null);
  const current = useRef(null);
  const seen = useRef(new Set());
  const timer = useRef(null);
  const serial = useRef(0);
  const advance = useRef(onAdvance);
  advance.current = onAdvance;

  const cancel = useCallback(() => {
    clearTimeout(timer.current);
    current.current = null;
    setReaction(null);
  }, []);
  useEffect(() => { seen.current.clear(); cancel(); return () => clearTimeout(timer.current); }, [scope, cancel]);
  useEffect(() => { if (!active) cancel(); }, [active, cancel]);
  const complete = useCallback(id => {
    const pending = current.current;
    if (!pending || pending.id !== id) return;
    clearTimeout(timer.current);
    current.current = null;
    setReaction(null);
    if (pending.nextHole != null) advance.current(pending.nextHole);
  }, []);
  useEffect(() => { if (!enabled && current.current) complete(current.current.id); }, [enabled, complete]);

  const confirmed = ({ index, previous, value, par, hole, nextHole }) => {
    if (!active) return;
    if (hasScore(previous)) { seen.current.add(index); return; }
    if (!hasScore(value) || seen.current.has(index) || current.current) return;
    seen.current.add(index);
    const result = enabled ? scoreResult(value, par) : null;
    const pending = { id: ++serial.current, result, hole, nextHole, scope };
    current.current = pending;
    if (result) {
      setReaction(pending);
      // Last-resort release if browser rendering/media fails before the player mounts.
      timer.current = setTimeout(() => complete(pending.id), 9000);
    } else {
      timer.current = setTimeout(() => complete(pending.id), 150);
    }
  };
  return { reaction: active && reaction?.scope === scope ? reaction : null, confirmed, complete, cancel };
};